import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Shield, UserPlus, Edit2, Trash2, Check, X, Crown, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminsManager = () => {
  const [admins, setAdmins] = useState([]);
  const [rolesInfo, setRolesInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'admin'
  });
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role: '',
    is_active: true
  });

  const roleColors = {
    super_admin: 'bg-purple-100 text-purple-800',
    admin: 'bg-blue-100 text-blue-800',
    manager: 'bg-green-100 text-green-800'
  };

  const roleIcons = {
    super_admin: Crown,
    admin: Shield,
    manager: User
  };

  useEffect(() => {
    fetchAdmins();
    fetchRolesInfo();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/management/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas les droits pour accéder à cette page');
      } else {
        toast.error('Erreur lors du chargement des administrateurs');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRolesInfo = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/management/roles/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRolesInfo(response.data);
    } catch (error) {
      console.error('Error fetching roles info:', error);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!createForm.password || createForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('email', createForm.email);
      formData.append('password', createForm.password);
      formData.append('full_name', createForm.full_name);
      if (createForm.phone) formData.append('phone', createForm.phone);
      formData.append('role', createForm.role);

      await axios.post(`${API}/admin/management/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Administrateur créé avec succès');
      setShowCreateForm(false);
      setCreateForm({
        email: '',
        password: '',
        full_name: '',
        phone: '',
        role: 'admin'
      });
      fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin.id);
    setEditForm({
      full_name: admin.full_name,
      email: admin.email,
      phone: admin.phone || '',
      password: '',
      role: admin.role,
      is_active: admin.is_active
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      
      if (editForm.full_name) formData.append('full_name', editForm.full_name);
      if (editForm.email) formData.append('email', editForm.email);
      if (editForm.phone !== undefined) formData.append('phone', editForm.phone);
      if (editForm.password) formData.append('password', editForm.password);
      if (editForm.role) formData.append('role', editForm.role);
      formData.append('is_active', editForm.is_active);

      await axios.patch(`${API}/admin/management/${editingAdmin}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Administrateur mis à jour avec succès');
      setShowEditModal(false);
      setEditingAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error editing admin:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteAdmin = async (adminId, adminName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'administrateur "${adminName}" ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/management/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Administrateur supprimé avec succès');
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isSuperAdmin = rolesInfo?.current_role === 'super_admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Gestion des Administrateurs</h1>
          <p className="text-slate-600">Gérez les comptes administrateurs et leurs droits</p>
        </div>
        {isSuperAdmin && (
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-purple-900 hover:bg-purple-800"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Créer un administrateur
          </Button>
        )}
      </div>

      {!isSuperAdmin && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-900">
              🔒 Vous pouvez consulter la liste des administrateurs, mais seuls les super administrateurs peuvent en créer ou modifier.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Roles Info */}
      {rolesInfo && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(rolesInfo.roles).map(([roleKey, roleData]) => {
            const Icon = roleIcons[roleKey];
            return (
              <Card key={roleKey}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-3 rounded-full ${roleColors[roleKey]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{roleData.name}</h3>
                      <p className="text-xs text-slate-500">
                        {admins.filter(a => a.role === roleKey).length} compte(s)
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{roleData.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Créer un nouvel administrateur</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="create-name">Nom complet *</Label>
                  <Input
                    id="create-name"
                    value={createForm.full_name}
                    onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="create-email">Email *</Label>
                  <Input
                    id="create-email"
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="admin@example.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="create-phone">Téléphone</Label>
                  <Input
                    id="create-phone"
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
                <div>
                  <Label htmlFor="create-password">Mot de passe *</Label>
                  <Input
                    id="create-password"
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    placeholder="Minimum 6 caractères"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="create-role">Rôle *</Label>
                  <select
                    id="create-role"
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    {rolesInfo && Object.entries(rolesInfo.roles).map(([roleKey, roleData]) => (
                      <option key={roleKey} value={roleKey}>{roleData.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-purple-900 hover:bg-purple-800">
                  <Check className="h-4 w-4 mr-2" />
                  Créer
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  <X className="h-4 w-4 mr-2" />
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Admins List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Liste des administrateurs ({admins.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-slate-500">Chargement...</p>
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500">Aucun administrateur trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Administrateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rôle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Créé le</th>
                    {isSuperAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {admins.map((admin) => {
                    const RoleIcon = roleIcons[admin.role];
                    return (
                      <tr key={admin.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-slate-900">{admin.full_name}</div>
                            <div className="text-sm text-slate-500">{admin.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-600">
                            {admin.phone || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={`${roleColors[admin.role]} flex items-center gap-1 w-fit`}>
                            <RoleIcon className="h-3 w-3" />
                            {rolesInfo?.roles[admin.role]?.name}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={admin.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {admin.is_active ? 'Actif' : 'Inactif'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {formatDate(admin.created_at)}
                        </td>
                        {isSuperAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditAdmin(admin)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteAdmin(admin.id, admin.full_name)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Éditer l'administrateur</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveEdit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-name">Nom complet</Label>
                    <Input
                      id="edit-name"
                      value={editForm.full_name}
                      onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-email">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-phone">Téléphone</Label>
                    <Input
                      id="edit-phone"
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-password">Nouveau mot de passe</Label>
                    <Input
                      id="edit-password"
                      type="password"
                      value={editForm.password}
                      onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      placeholder="Laisser vide pour ne pas changer"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-role">Rôle</Label>
                    <select
                      id="edit-role"
                      value={editForm.role}
                      onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {rolesInfo && Object.entries(rolesInfo.roles).map(([roleKey, roleData]) => (
                        <option key={roleKey} value={roleKey}>{roleData.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit-active"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <Label htmlFor="edit-active">Compte actif</Label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-purple-900 hover:bg-purple-800">
                    <Check className="h-4 w-4 mr-2" />
                    Enregistrer
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingAdmin(null);
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Annuler
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminsManager;
